import React from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'
import ssrPrepass from 'react-ssr-prepass'
import {dehydrate, Hydrate, QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {FetchStrategy} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/fetch-strategy'
import {IFRAME_HOST_ALLOW_LIST} from '@salesforce/commerce-sdk-react/constant'
import logger from '../utils/logger-instance'

const STATE_KEY = '__reactQuery'
const passthrough = (input) => input

/**
 * A HoC for adding React Query support to your application and handling waterfall queries.
 *
 * based on withReactQuery from pwa-kit-react-sdk
 *
 * @param {React.ReactElement} Wrapped The component to be wrapped
 * @param {Object} options
 * @param {Object} options.queryClientConfig The react query client configuration object to be used.
 *
 * @returns {React.ReactElement}
 */
export const withWaterfallReactQuery = (Wrapped, options = {}) => {
    const isServerSide = typeof window === 'undefined'
    /* istanbul ignore next */
    const wrappedComponentName = Wrapped.displayName || Wrapped.name
    const queryClientConfig = options.queryClientConfig
    const beforeHydrate = options.beforeHydrate || passthrough
    const maxWaterfallDepth = options.maxWaterfallDepth || 5
    const warnWaterfallTime = options.warnWaterfallTime || 2500
    const warnPrepassTime = options.warnPrepassTime || 250
    const warnQueryTime = options.warnQueryTime || 1000

    /**
     * @private
     */
    class WithWaterfallReactQuery extends FetchStrategy {
        render() {
            let preloadedState = {}

            this.props.locals.__queryClient =
                this.props.locals.__queryClient || new QueryClient(queryClientConfig)

            if (!isServerSide) {
                try {
                    preloadedState = beforeHydrate(window.__PRELOADED_STATE__?.[STATE_KEY] || {})
                } catch (e) {
                    logger.error('Client `beforeHydrate` failed', {
                        namespace: 'with-react-query.render',
                        additionalProperties: {error: e}
                    })
                }
            }

            return (
                <QueryClientProvider client={this.props.locals.__queryClient}>
                    <Hydrate state={preloadedState}>
                        <Wrapped {...this.props} />
                    </Hydrate>
                </QueryClientProvider>
            )
        }

        /**
         * @private
         */
        static async doInitAppState({req, res, appJSX}) {
            const skipQueries =
                // forced client-side rendering
                '__client_only' in req.query ||
                // if the limitOutput flag is set, we should not fetch any data to avoid excessive response sizes
                res.locals?.limitOutput ||
                // skip server-side rendering if the referer is in the iframe allow list (i.e. preview mode)
                // as we don't have access to the modified shopper context and will prerender a different page
                (req.headers.referer &&
                    IFRAME_HOST_ALLOW_LIST.includes(new URL(req.headers.referer).origin))

            const isActiveQuery = (q) =>
                q.options.enabled !== false &&
                q.state.status === 'loading' &&
                (!skipQueries || q.options.forceOnServer)

            const queryClient = (res.locals.__queryClient =
                res.locals.__queryClient || new QueryClient(queryClientConfig))

            const queryCache = queryClient.getQueryCache()

            const __startWaterfall = Date.now()

            // Fetch all pending queries in a waterfall pattern
            for (let depth = 0; depth < maxWaterfallDepth; depth++) {
                // Use `ssrPrepass` to collect all uses of `useQuery`.
                const __startPrepass = Date.now()
                await ssrPrepass(appJSX)
                const __endPrepass = Date.now()
                if (__endPrepass - __startPrepass > warnPrepassTime) {
                    console.warn(
                        `Slow prepass during iteration ${depth + 1} - ${
                            __endPrepass - __startPrepass
                        }ms`
                    )
                }

                // exit in case we are redirected as the result doesn't matter in that case
                if (appJSX?.props?.routerContext?.url) {
                    return
                }

                const queries = queryCache.getAll().filter(isActiveQuery)

                // short-circuit if there are no more pending queries
                if (!queries.length) {
                    break
                }

                await Promise.all(
                    queries.map((q) => {
                        const __startQuery = Date.now()
                        // If there's an error in this fetch, react-query will log the error
                        return q
                            .fetch()
                            .catch(() => {
                                // On our end, simply catch any error and move on to the next query
                            })
                            .finally(() => {
                                const __endQuery = Date.now()
                                if (__endQuery - __startQuery > warnQueryTime) {
                                    console.warn(
                                        `Slow query ${q.queryHash} during iteration ${
                                            depth + 1
                                        } - ${__endQuery - __startQuery}ms`
                                    )
                                }
                            })
                    })
                )
            }

            const __endWaterfall = Date.now()
            if (__endWaterfall - __startWaterfall > warnWaterfallTime) {
                console.warn(
                    `Slow fetching all pending queries - ${__endWaterfall - __startWaterfall}ms`
                )
            }

            if (queryCache.getAll().some(isActiveQuery)) {
                console.error(
                    `Failed to fetch all pending queries in ${maxWaterfallDepth} iterations`
                )
            }

            return {[STATE_KEY]: dehydrate(queryClient)}
        }

        /**
         * @private
         */
        static getInitializers() {
            return [WithWaterfallReactQuery.doInitAppState, ...(Wrapped.getInitializers?.() ?? [])]
        }

        /**
         * @private
         */
        static getHOCsInUse() {
            return [withWaterfallReactQuery, ...(Wrapped.getHOCsInUse?.() ?? [])]
        }
    }

    WithWaterfallReactQuery.displayName = `withWaterfallReactQuery(${wrappedComponentName})`

    const exclude = {
        doInitAppState: true,
        getInitializers: true,
        initAppState: true,
        getHOCsInUse: true
    }
    hoistNonReactStatic(WithWaterfallReactQuery, Wrapped, exclude)

    return WithWaterfallReactQuery
}
